/**
 * Integrytas Group — RowActionsMenu (common).
 *
 * Overflow ("⋯") menu for grid row actions (P-07). Rows with 3-4 always-
 * visible bordered buttons produce ~100 identical buttons per screen and,
 * because the set is state-dependent, buttons jump horizontally between
 * rows. The convention instead: at most ONE inline primary verb per row
 * (the row's dominant action — View, Receive, …) and everything else in
 * this fixed-width menu, with unavailable actions DISABLED rather than
 * hidden so the item positions never change between rows.
 *
 * Usage:
 *   <RowActionsMenu
 *     label={t('common.row_actions', 'Row actions')}
 *     actions={[
 *       { key: 'view',   label: t('common.view', 'View'),     onSelect: () => openView(row) },
 *       { key: 'close',  label: t('po.close', 'Close'),       onSelect: () => closePo(row),  disabled: row.status_code !== 'RECEIVED' },
 *       { key: 'reopen', label: t('po.reopen', 'Reopen'),     onSelect: () => reopenPo(row), disabled: row.status_code !== 'CLOSED', danger: false },
 *     ]}
 *   />
 */

import { useEffect, useRef, useState } from 'react';

export interface RowAction {
  key: string;
  /** Already-localized label. */
  label: string;
  onSelect: () => void;
  /** Disabled items stay VISIBLE (grayed) so menu positions are stable across rows. */
  disabled?: boolean;
  /** Destructive styling (e.g. Delete, Write Off). */
  danger?: boolean;
}

export interface RowActionsMenuProps {
  actions: RowAction[];
  /** aria-label for the trigger. Pass a translated string. */
  label: string;
}

export function RowActionsMenu({ actions, label }: RowActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); setOpen(false); }
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative inline-block">
      <button
        type="button"
        aria-label={label}
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        className="flex h-7 w-7 items-center justify-center rounded-csm-icon border border-transparent text-csm-ink-soft hover:border-csm-border hover:bg-csm-surface-alt hover:text-csm-ink"
      >
        {/* Horizontal ellipsis glyph — sized by font, no icon dependency */}
        <span aria-hidden="true" className="text-csm-md leading-none">⋯</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 z-30 mt-1 min-w-[10rem] rounded-csm-card border border-csm-border bg-white py-1 elev-1 anim-pop"
        >
          {actions.map(a => (
            <button
              key={a.key}
              type="button"
              role="menuitem"
              disabled={a.disabled}
              onClick={(e) => {
                e.stopPropagation();
                if (a.disabled) return;
                setOpen(false);
                a.onSelect();
              }}
              className={
                'block w-full px-3 py-1 text-left text-csm-sm ' +
                (a.disabled
                  ? 'cursor-not-allowed text-csm-muted opacity-60'
                  : a.danger
                    ? 'text-csm-danger hover:bg-csm-danger-soft'
                    : 'text-csm-ink hover:bg-csm-surface-alt')
              }
            >
              {a.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
